import { ApiError } from "@groundspark/browser-auth";
import { button, buttonIcon, element, heading } from "@groundspark/browser-ui";
import { Autosave, type SaveState } from "./autosave";
import { breadcrumbs } from "./breadcrumbs";
import type { BranchApi } from "./branch-api";
import type { Leaflet, LeafletDocument } from "./domain";
import { LeafletBitmaps } from "./leaflet-bitmaps";
import type { LeafletEditor } from "./leaflet-editor";
import { leafletSettingsDialog } from "./leaflet-settings-dialog";
import {
  MAXIMUM_PAGE_BYTES,
  type Pages,
  pageBitmaps,
  pageBytes,
  paperSizeLabels,
  sideLabels,
  sides,
} from "./leaflet-pages";

/** Each save stores its changed pages anew, so saves wait for a longer pause than text drafts. */
const SAVE_DELAY = 2_000;

export class LeafletWorkspace {
  public readonly element = element("section", "composer leaflet-workspace");
  private readonly body = element("div", "leaflet-workspace__body");
  private editor: LeafletEditor | null = null;
  /** The latest saved state, including where the QR code leads. */
  private leaflet: Leaflet;
  private readonly bitmaps = new LeafletBitmaps();
  /** The server keeps the bitmaps that saved pages draw. */
  private savedBitmaps: ReadonlySet<string> = new Set();
  private readonly loading = new AbortController();
  /** The open settings dialog, which closes with the workspace. */
  private settings: AbortController | null = null;
  private readonly autosave: Autosave<Pages, Leaflet>;
  private disposed = false;
  private readonly saveStatus = element("p", "muted", "Leaflet saved");
  private readonly saveError = element("p", "composer__error");
  private readonly retry = button("Retry saving", "button button--white", () => {
    void this.save();
  });
  private readonly reload = button("Reload saved leaflet", "button button--white", () => {
    if (window.confirm("Reload the saved leaflet? Your unsaved changes will be replaced."))
      void this.onReload();
  });
  private readonly beforeUnload = (event: BeforeUnloadEvent): void => {
    if (this.autosave.dirty) {
      void this.save();
      event.preventDefault();
      event.returnValue = "";
    }
  };
  private readonly visibilityChange = (): void => {
    if (document.visibilityState === "hidden") void this.save();
  };
  private readonly online = (): void => {
    void this.save();
  };

  public constructor(
    leaflet: LeafletDocument,
    private readonly branchId: string,
    private readonly api: BranchApi,
    onLeaflets: () => void | Promise<void>,
    private readonly onSaved: (leaflet: Leaflet) => void,
    private readonly onReload: () => Promise<void>,
    private readonly onFailure: (error: unknown) => boolean,
  ) {
    const { pages: _pages, ...summary } = leaflet;
    this.leaflet = summary;
    this.saveStatus.setAttribute("role", "status");
    this.saveError.setAttribute("role", "alert");
    this.retry.prepend(buttonIcon("save"));
    this.reload.prepend(buttonIcon("reload"));
    this.retry.hidden = true;
    this.reload.hidden = true;
    this.autosave = new Autosave(
      leaflet.revision,
      () => this.pages(),
      async (revision, pages) => {
        const bitmaps = pageBitmaps(pages);
        // The server only keeps bitmaps that saved pages draw; others are uploaded first.
        for (const reference of bitmaps)
          if (!this.savedBitmaps.has(reference))
            await api.uploadLeafletBitmap(branchId, reference, this.bitmaps.blob(reference));
        const saved = await api.saveLeaflet(branchId, leaflet.id, revision, pages);
        this.savedBitmaps = bitmaps;
        return saved;
      },
      (saved) => this.remember(saved),
      (state, error) => this.showSaveState(state, error),
      SAVE_DELAY,
    );
    const settingsButton = button("Settings", "button button--white", () =>
      this.openSettings(),
    );
    settingsButton.prepend(buttonIcon("settings"));
    const title = heading(2, leaflet.name, "panel-heading__title");
    const loading = element("p", "branch-loading", "Opening the leaflet editor…");
    loading.setAttribute("role", "status");
    this.body.append(loading);
    this.element.append(
      element(
        "header",
        "composer__navigation",
        breadcrumbs(leaflet.name, [{ label: "Leaflets", onSelect: onLeaflets }]),
        element(
          "div",
          "composer__controls",
          this.saveStatus,
          this.retry,
          this.reload,
          settingsButton,
        ),
      ),
      this.saveError,
      element(
        "div",
        "panel-heading",
        title,
        element(
          "p",
          "muted",
          `${paperSizeLabels[leaflet.paperSize]}, front and back, with 3 mm bleed`,
        ),
      ),
      this.body,
    );
    window.addEventListener("beforeunload", this.beforeUnload);
    window.addEventListener("online", this.online);
    document.addEventListener("visibilitychange", this.visibilityChange);
    void this.open(leaflet, branchId, api);
  }

  /** Resolves once every edit is saved; `false` keeps the editor open. */
  public save(): Promise<boolean> {
    return this.autosave.flush();
  }

  public destroy(): void {
    this.disposed = true;
    this.loading.abort();
    this.settings?.abort();
    this.autosave.destroy();
    window.removeEventListener("beforeunload", this.beforeUnload);
    window.removeEventListener("online", this.online);
    document.removeEventListener("visibilitychange", this.visibilityChange);
    this.editor?.destroy();
    this.editor = null;
    this.bitmaps.destroy();
    this.element.replaceChildren();
  }

  private async open(
    leaflet: LeafletDocument,
    branchId: string,
    api: BranchApi,
  ): Promise<void> {
    try {
      const references = pageBitmaps(leaflet.pages);
      // Fabric is large, so it loads only when a leaflet opens.
      const [{ createLeafletEditor }] = await Promise.all([
        import("./leaflet-editor"),
        ...[...references].map(async (reference) => {
          const bitmap = await api.leafletBitmap(branchId, reference, this.loading.signal);
          if ((await this.bitmaps.add(bitmap)) !== reference)
            throw new Error("An image on this leaflet is damaged.");
        }),
      ]);
      if (this.disposed) return;
      this.savedBitmaps = references;
      // A leaflet that has never been saved starts with a QR code on its back page.
      const editor = await createLeafletEditor(
        leaflet.paperSize,
        leaflet.pages,
        this.bitmaps,
        leaflet.qrCode.url,
        leaflet.revision === 1,
        () => this.autosave.change(),
      );
      if (this.disposed) {
        editor.destroy();
        return;
      }
      this.editor = editor;
      this.body.replaceChildren(editor.element);
    } catch (error) {
      if (this.disposed) return;
      const failure = element(
        "p",
        "composer__error",
        `This leaflet could not be opened. ${error instanceof Error ? error.message : String(error)}`,
      );
      failure.setAttribute("role", "alert");
      this.body.replaceChildren(failure);
    }
  }

  private remember(leaflet: Leaflet): void {
    this.leaflet = leaflet;
    this.onSaved(leaflet);
  }

  private async openSettings(): Promise<void> {
    if (this.settings !== null) return;
    const request = new AbortController();
    this.settings = request;
    try {
      const saved = await leafletSettingsDialog(
        this.leaflet,
        async (destination) => {
          try {
            return await this.api.setLeafletQrDestination(
              this.branchId,
              this.leaflet.id,
              destination,
              request.signal,
            );
          } catch (error) {
            // A leaflet deleted elsewhere keeps its editor open, as when saving pages.
            if (!(error instanceof ApiError && error.status === 404) && this.onFailure(error))
              request.abort();
            throw error;
          }
        },
        request.signal,
      );
      if (saved !== null && !this.disposed) this.remember(saved);
    } finally {
      this.settings = null;
    }
  }

  private pages(): Pages {
    if (this.editor === null) throw new Error("The leaflet editor is not ready.");
    const pages = this.editor.pages();
    for (const side of sides) {
      const bytes = pageBytes(pages[side]);
      if (bytes > MAXIMUM_PAGE_BYTES)
        throw new Error(
          `The ${sideLabels[side].toLowerCase()} page is ${megabytes(bytes)} MB; pages can be at most ${megabytes(MAXIMUM_PAGE_BYTES)} MB. Remove some of its content.`,
        );
    }
    return pages;
  }

  private showSaveState(state: SaveState, error?: unknown): void {
    const labels = {
      saved: "Leaflet saved",
      waiting: "Unsaved changes",
      saving: "Saving leaflet…",
      error: "Leaflet not saved",
    };
    this.saveStatus.textContent = labels[state];
    this.saveError.textContent =
      state === "error"
        ? `Your changes are still in this editor. ${error instanceof Error ? error.message : String(error)}`
        : "";
    const conflict = state === "error" && error instanceof ApiError && error.status === 409;
    this.retry.hidden = state !== "error" || conflict;
    this.reload.hidden = !conflict;
    // A leaflet may have been deleted elsewhere; retain local changes.
    if (state === "error" && !(error instanceof ApiError && error.status === 404))
      this.onFailure(error);
  }
}

function megabytes(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}
